import React from 'react';
import { useTranslation } from 'react-i18next';
import { ListColumnType } from 'types/ListTypes';
import AbstractList from 'components/lists/AbstractList';
import ApiKeyForm from './ApiKeyForm';
import ApiTierOrderModal from './ApiTierOrderModal';
import { useModal } from 'components/modals/ModalContext';
import { useAuthContext } from 'services/authentication/AuthenticationContext';
import { canOrderPaidFeatures } from '../billing/functions/billingUtils';
import { useSettingsDivision } from 'components/settings/SettingsDivisionContext';

// Rename the name of the function:
const ApiKeyList: React.FC = () => {
    const { t } = useTranslation();
    const { initializeModal } = useModal();
    const { environmentType } = useAuthContext();

    // Filter the list on the division selected in the settings menu
    const { selectedDivision, isEnvironmentSelected } = useSettingsDivision();
    const divisionParams = !isEnvironmentSelected && selectedDivision ? { 'division_scope': selectedDivision.id } : {};

    // Specify the options of this list:
    const query = {
        endpoint: 'get_api_token_list',
        defaultParams: { ...divisionParams },
        object: 'api_token'
    }
    const objectName = 'api_key.general.object_name'
    const showSearch = false
    const showDeleteOrDeactivate = 'fully_delete'

    // Set isPaginated to true if this list may have 100.000+ items:
    const isPaginated = false

    // Specify form, detail modal or detailpage for this list
    const formModal = <ApiKeyForm />;

    // Only environment types with a paid subscription can order api access
    const canUpgradeTier = canOrderPaidFeatures(environmentType);

    // Open the api tier order modal for the given api token
    const openTierOrderModal = (event: React.MouseEvent, tokenId: number) => {
        event.stopPropagation();
        initializeModal(
            <ApiTierOrderModal tokenId={tokenId} />,
            { modalSize: 'medium' }
        );
    };

    // Specify the columns of this list:
    const columns: ListColumnType[] = [
        { field: 'token', label: 'api_key.general.key_label', width: '2fr' },
        { field: 'name', label: '', width: '1fr', },
        {
            field: 'rate_limit_tier',
            label: 'api_key.general.tier_label',
            width: '2fr',
            render: (value, item) => (
                <span>
                    ({t(`api_key.tier.${value}`)} {t('api_key.general.level_suffix')})
                    {value !== 'extended' && canUpgradeTier && (
                        // Show an upgrade link behind the level of free keys
                        <span
                            className='module-order-link upgrade-tier-link'
                            onClick={(event) => openTierOrderModal(event, item.id)}
                        >
                            {t('api_key.general.upgrade_tier_label')}
                        </span>
                    )}
                </span>
            )
        },
    ];

    return (
        <AbstractList
            key={!isEnvironmentSelected && selectedDivision ? selectedDivision.id : 'environment'}
            query={query}
            objectName={objectName}
            columns={columns}
            showSearch={showSearch}
            showDeleteOrDeactivate={showDeleteOrDeactivate}
            formModal={formModal}
            isPaginated={isPaginated}
        />
    );
};

export default ApiKeyList;
